<template>
  <div class="atomic-menu-category">
    <atomic-menu-category-activator
      ref="categoryActivatorRef"
      :items="items"
      :icon="icon"
      :label="label"
      :url="url"
      :isOpen="isOpen"
      :isDrawerCompact="isDrawerCompact"
      @toggle="handlerMenu"
      @mouseover="showHoverDropdown()"
      @mouseleave="hideHoverDropdown()"
    >
      <slot name="activator" />
    </atomic-menu-category-activator>

    <div class="atomic-menu-category__dropdown" :style="styles" ref="contentRef">
      <div class="atomic-menu-category__divider" v-if="!isDrawerCompact || !closeOnCompact" />

      <atomic-menu-category-dropdown :isDrawerCompact="isDrawerCompact" :items="items" />
    </div>

    <Teleport to="body">
      <div
        v-if="isDrawerCompact && hasHoverDropdown"
        :class="{ 'is-active': isShowHoverDropdown }"
        ref="hoverDropdownRef"
        class="atomic-menu-category__hover-dropdown"
        :style="`top: ${coords.top}px; left: ${coords.left}px`"
        @mouseover="showHoverDropdown()"
        @mouseleave="hideHoverDropdown()"
      >
        <div class="atomic-menu-category__hover-dropdown-container" >
          <atomic-link
            class="atomic-menu-category__hover-dropdown-link"
            v-for="link in items"
            :key="link.label"
            :href="link.url"
            :targetBlank="link?.targetBlank"
          >
            <span class="text">{{ link.label }}</span>
          </atomic-link>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import type { IIconLink } from '~/types';

  const props = defineProps<{
    label: string;
    icon: string;
    url: string;
    items: IIconLink[];
    hiddenDropdown?: boolean;
    isDrawerCompact: boolean;
    closeOnCompact?: boolean;
    hasHoverDropdown?: boolean;
  }>();

  const isOpen = ref(true);

  const contentRef = ref<HTMLElement | null>(null);
  const categoryActivatorRef = ref<HTMLElement | null>(null);
  const hoverDropdownRef = ref<HTMLElement | null>(null);
  const isShowHoverDropdown = ref(false);
  const heightStyle = ref('0px');

  const styles = computed(() => {
    return { height: props.hiddenDropdown ? '0px' : heightStyle.value };
  });

  const coords = reactive({
    top: 0,
    left: 0,
  });

  const showHoverDropdown = () => {
    if (!props.isDrawerCompact || !props.hasHoverDropdown || !categoryActivatorRef.value) return;

    setCoords();
    isShowHoverDropdown.value = true;
  };

  const hideHoverDropdown = () => {
    if (!props.isDrawerCompact || !props.hasHoverDropdown) return;

    isShowHoverDropdown.value = false;
    coords.top = 0;
    coords.left = 0;
  };

  const setCoords = () => {
    const categoryActivatorRect =  categoryActivatorRef.value?.$el?.getBoundingClientRect();

    if (categoryActivatorRect) {
      coords.left = categoryActivatorRect.x + categoryActivatorRect.width -1;

      if (hoverDropdownRef.value) {
        const dropdownRect = hoverDropdownRef.value.getBoundingClientRect();
        const dropdownHeight = dropdownRect.height;

        const viewportHeight = window.innerHeight;

        if (categoryActivatorRect.y + dropdownHeight > viewportHeight) {
          coords.top = viewportHeight - dropdownHeight;
        } else {
          coords.top = categoryActivatorRect.y;
        }
      }
    }
  };

  const handlerMenu = async () => {
    if (!contentRef.value) return;

    if (isOpen.value) {
      heightStyle.value = `${contentRef.value.scrollHeight}px`;
      requestAnimationFrame(() => {
        heightStyle.value = '0px';
      });
    } else {
      heightStyle.value = '0px';
      requestAnimationFrame(() => {
        if (contentRef.value) {
          heightStyle.value = `${contentRef.value.scrollHeight}px`;
        }
      });
    }

    isOpen.value = !isOpen.value;
  };

  watch(
    () => props.isDrawerCompact,
    (newValue: any) => {
      if (props.closeOnCompact) {
        isOpen.value = newValue;
        handlerMenu();
      }

      if (!props.closeOnCompact && newValue) {
        isOpen.value = false;
        handlerMenu();
      }
    },
  );

  onMounted(() => {
    if (isOpen.value && contentRef.value) {
      heightStyle.value = `${contentRef.value.scrollHeight}px`;
    }
  });
</script>

<style src="~/assets/styles/components/nav/menu/category.scss" lang="scss" />
